import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso';
import FlexRow from "../../../components/base/FlexRow";
import ChatContactItem from "../models/ChatContactItem";
import { clearSystemChatUnread, getUser, hasRole, isDemo, openBrowserPhone, openChatQuickReplies, setChatList, useChatList, useGlobal, useHub, usePhone, useQuickReplies, useScreen, useSocket } from "../../../core/Global";
import { API_ROUTES } from "../../../core/Constants";
import ChatMessage from "../models/ChatMessage";
import ChatMessageItem from "./ChatMessageItem";
import ChatTypingBubble from "./ChatTypingBubble";
import { CompactJob, CompactUser } from "../../../core/CompactModels";
import Icon from "../../../components/base/Icon";
import ChatContactContextMenu, { CHAT_BLOCK_CHANGED_EVENT, unblockChat } from "./ChatContactContextMenu";
import Indicator from "../../../components/base/Indicator";
import IconButton from "../../../components/base/IconButton";
import { v4 as uuidv4 } from 'uuid';
import ChatAvatarSpan from "./ChatAvatarSpan";
import { toastError } from "../../../components/base/Toast";
import { ChatEnvironments, MediaTypes, UserTypes } from "../../../core/Enums";
import { Link } from "react-router-dom";
import TechWorkingHoursDrawer from "./TechWorkingHoursDrawer";
import TechClientSessionBar from "./TechClientSessionBar";
import VoiceRecorder from "../../../components/chat/VoiceRecorder";
import { isNativeApp } from "../../../NativeAppBrdige";

interface IProps {
    chat?: ChatContactItem
    chatId?: string
    inDrawer?: boolean
    inBottomsheet?: boolean
    parentId?: string
    jobId?: string
    clientChatJobId?: string
    onSearch?: (ch: ChatContactItem) => void
    onRead?: () => void
    onPin?: () => void
    onUnpin?: () => void
    onUnarchive?: () => void
    onAssignMessageToJob?: (msg: ChatMessage) => void
    onMessageSending?: (msg: ChatMessage) => void
    onMessageSent?: (ch: ChatContactItem, msg: ChatMessage) => void
}

export default function ChatBox(props: IProps) {


    let chatAttachmentInput: HTMLInputElement | null;


    const hub = useHub()
    const screen = useScreen()
    const [globalState, globalStateSet] = useGlobal()
    const socket = useSocket()
    const user = getUser()
    const replies = useQuickReplies()
    const phone = usePhone()

    const [phoneReady, phoneReadySet] = useState<boolean>(
        !!phone.getLines()?.some(l => l.registrationStatus === "registered" && l.transportConnected)
    )

    useEffect(() => {
        const unsub = phone.subscribe(state => {
            phoneReadySet(state.lines.some(l => l.registrationStatus === "registered" && l.transportConnected))
        })
        return () => unsub()
    }, [])

    const canCallContact = phoneReady && !props.chat?.isGroup && !!props.chat?.recipientPhone

    const chatTextArea = useRef<HTMLTextAreaElement>(null)
    //const chatContainer = useRef<VirtuosoHandle>(null)
    const chatContainerDiv = useRef<HTMLDivElement>(null)

    const [typingUsers, typingUsersSet] = useState<CompactUser[]>([]);
    /* One timer per person typing: they are taken off after a few seconds of hearing nothing,
       because a StopTyping that never arrives (an older app build, a dropped connection, a phone
       put away mid-word) would otherwise leave the dots up for good. */
    const typingTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
    const clearTypingTimer = (id?: string) => {
        if (!id) return;
        const timer = typingTimers.current.get(id);
        if (timer) clearTimeout(timer);
        typingTimers.current.delete(id);
    };
    const [appVisible, appVisibleSet] = useState<boolean>(document.visibilityState == "visible");
    const [boxHeight, boxHeightSet] = useState<number>();

    const fixSizes = () => {

        var w = screen.width;
        var h = screen.height;

        if (props.inDrawer) {
            h = h - ($("#kt_drawer_job_header").outerHeight() ?? 0);
            h = h - ($("#kt_drawer_job_tabs").outerHeight() ?? 0);
        }
        else if (props.inBottomsheet) {
            h = screen.height - 100;
        }
        else {
            h = h - ($("#kt_header").outerHeight() ?? 0);

            //h = h - ($("#kt_toolbar").outerHeight() ?? 0);
            //if (!screen.isMobile) h = h - 40;
        }

        h = h - ($("#kt_chat_messenger_header").outerHeight() ?? 0);
        h = h - ($("#kt_chat_messenger_footer").outerHeight() ?? 0);

        // The technician-session bar is part of the chrome, not of the thread. Left out of this,
        // it pushed the messages down by its own height and the last of them off the bottom —
        // the box is sized to fit the screen exactly so that it, and not the page, scrolls.
        h = h - ($("#kt_chat_tech_session_bar").outerHeight() ?? 0);

        // Mobile bottom navbar is covered by drawer / bottomsheet on those modes,
        // so only subtract it when chat is rendered in the normal page flow.
        if (!props.inDrawer && !props.inBottomsheet) {
            h = h - ($("#mobile-tabs-bottom-navbar").outerHeight() ?? 0);
        }

        h = h - 6
        boxHeightSet(h);
    }

    const onVisibilityChanged = () => {
        appVisibleSet(document.visibilityState == "visible");
    }

    useEffect(() => {

        fixSizes();

        // After mount, the parent drawer/header may not be measurable yet
        // (esp. on first paint or page refresh). Re-run a few times to catch
        // the real dimensions once the surrounding layout settles.
        const retries = [50, 150, 400, 800].map(delay => setTimeout(fixSizes, delay));

        window.addEventListener("resize", fixSizes);
        window.addEventListener("visibilitychange", onVisibilityChanged);

        return () => {
            retries.forEach(clearTimeout);
            window.removeEventListener("resize", fixSizes);
            window.removeEventListener("visibilitychange", onVisibilityChanged);
        }

    }, [])

    const [scrolling, scrollingSet] = useState<boolean>(false)
    const [claiming, claimingSet] = useState<boolean>(false)
    const [loading, loadingSet] = useState<boolean>(false)
    const [loaded, loadedSet] = useState<boolean>(false)
    const [messages, messagesSet] = useState<ChatMessage[]>([])
    const [noEarlierMessages, noEarlierMessagesSet] = useState<boolean>(false)
    const [earlierMessagesLoaded, earlierMessagesLoadedSet] = useState<boolean>(false)
    const [loadingEarlierMessages, loadingEarlierMessagesSet] = useState<boolean>(false)



    const fetchMessages = () => {

        loadingSet(true)
        loadingEarlierMessagesSet(false)
        noEarlierMessagesSet(false)
        var q = "";
        if (props.chat?.searchMsgItem) {
            q = "?searchMsgId=" + props.chat.id;
        }
        hub.Get(API_ROUTES.ChatMessages + props.chatId + q, res => {
            if (res.success) {
                messagesSet(res.data)
                loadingSet(false)
                loadedSet(true)
                noEarlierMessagesSet(false)
            }
        })
    }

    // const [scrollKeepIndex, scrollKeepIndexSet] = useState<number>()
    const [scrollKeepId, scrollKeepIdSet] = useState<string>()

    const [lastScrollTop, lastScrollTopSet] = useState<number>()

    const fetchEarlierMessages = () => {
        if (loading) return
        if (noEarlierMessages) return
        if (loadingEarlierMessages) return

        loadingEarlierMessagesSet(true)
        lastScrollTopSet(chatContainerDiv.current?.scrollTop)

        setTimeout(() => {

            var q = "?lastMessageId=" + messages[0].id;
            hub.Get(API_ROUTES.ChatMessages + props.chatId + q, res => {
                if (res.success && res.data.length > 0) {
                    messagesSet(msg => [...res.data, ...msg])
                    //scrollKeepIndexSet(res.data.length);
                    scrollKeepIdSet(res.data[res.data.length - 1].id)
                    earlierMessagesLoadedSet(true)
                }
                else {
                    noEarlierMessagesSet(true)
                    loadingEarlierMessagesSet(false)
                }
            })

        }, 500);

    }

    useEffect(() => {
        if (earlierMessagesLoaded && scrollKeepId) {

            const msgView = document.getElementById(`chatmsg-${scrollKeepId}`) as HTMLDivElement | null;
            if (msgView) {
                msgView.scrollIntoView({
                    behavior: "auto"
                })
            }

            if (lastScrollTop && chatContainerDiv.current) {

                // chatContainerDiv.current?.scrollTo({
                //     top: lastScrollTop,
                //     behavior: 'auto'
                // })

                // chatContainer.current.scrollToIndex({
                //     index: scrollKeepIndex + 1,
                //     align: 'end',
                //     behavior: 'auto'
                // });
            }

            if (scrollKeepId && screen.isMobile) {

            }




            earlierMessagesLoadedSet(false)
            //scrollKeepIndexSet(undefined)
            loadingEarlierMessagesSet(false)
        }
    }, [earlierMessagesLoaded, scrollKeepId])


    // The chat prop is a snapshot taken when the chat was opened; a block or unblock made while it
    // is open (from its menu or the list) arrives as an event and is kept here.
    const [blockedNow, blockedNowSet] = useState<boolean>();
    useEffect(() => {
        const onChange = (e: Event) => {
            const d = (e as CustomEvent).detail;
            if (d?.chatId && d.chatId == props.chatId) blockedNowSet(!!d.blocked);
        };
        window.addEventListener(CHAT_BLOCK_CHANGED_EVENT, onChange);
        return () => window.removeEventListener(CHAT_BLOCK_CHANGED_EVENT, onChange);
    }, [props.chatId])

    const [newMessage, newMessageSet] = useState<ChatMessage>();
    useEffect(() => {

        if (newMessage && user?.id) {
            if (newMessage.chatId != props.chatId) return;

            // Own messages are not skipped: the same user may have this chat open in another tab,
            // or have sent something from elsewhere (a job sent to the technician). The copy this
            // tab added optimistically shares the message's uuid, so the echo replaces it instead
            // of showing twice.
            const isOwn = newMessage.senderUserId == user.id;
            messagesSet(prev => {
                const list = prev ?? [];
                if (newMessage.id && list.some(m => m.id === newMessage.id)) return list;
                if (newMessage.uuid && list.some(m => m.uuid === newMessage.uuid))
                    return list.map(m => m.uuid === newMessage.uuid ? newMessage : m);
                return [...list, newMessage];
            });

            if (!scrolling || isOwn) {
                setTimeout(scrollToEnd, 100);
            }

            if (!isOwn) {
                setTimeout(() => {
                    hub.Get(API_ROUTES.ChatRead + props.chatId)
                }, 500);
            }
        }

    }, [newMessage, user])



    // The dots appear at the end of the thread, below whatever is on screen — so the reader is
    // taken there, and taken there again when they go and the list shrinks back. Not when they
    // have scrolled up to read something: their place is theirs.
    useEffect(() => {
        if (scrolling) return;
        setTimeout(() => scrollToEnd("smooth"), 60);
    }, [typingUsers.length])

    const scrollToEnd = (behavior: any) => {
        if (chatContainerDiv.current) {
            chatContainerDiv.current?.scrollTo({
                top: chatContainerDiv.current.scrollHeight,
                behavior: behavior ?? 'auto'
            });
        }

        // if (chatContainer.current) {
        //     chatContainer.current?.scrollToIndex({
        //         index: messages.length,
        //         align: 'end',
        //         behavior: behavior ?? 'auto'
        //     });
        // }

    }


    useEffect(() => {
        if (messages.length > 0 && globalState.gotoMessageId) {
            gotoMessage(globalState.gotoMessageId);
        }
    }, [messages, globalState.gotoMessageId])

    useEffect(() => {
        if (props.chat?.id) {

            fetchMessages();

            // Internal/system chat is now open and selection is stable: clear its unread badge locally.
            if (props.chat?.isSystem) clearSystemChatUnread(props.chatId);

            if (!screen.isMobile) {
                chatTextArea?.current?.focus();
            }

            if (!props.chat?.searchMsgItem) {

                var socketKey = socket.start(
                    skt => {
                        skt.invoke("StartListenToChat", props.chatId)
                            .then(() => {

                                skt.on("SendChatMessage-" + props.chatId, msg => {
                                    if (props.clientChatJobId && !msg.techClientChat) return;
                                    newMessageSet(msg)

                                    // Internal/system chat is open: persist read state for this user so the
                                    // message isn't counted as unread for them after a future reload.
                                    if (props.chat?.isSystem && msg.senderUser?.id !== user?.id) {
                                        hub.Get(API_ROUTES.Chat + "read/" + props.chatId, () => { });
                                    }
                                });

                                skt.on("UpdateChatMessage", newMsg => {
                                    if (props.clientChatJobId && !newMsg.techClientChat) return;
                                    if (newMsg.chatId == props.chatId) {
                                        messagesSet(prev =>
                                            prev.map(oldMsg =>
                                                oldMsg.id === newMsg.id ? newMsg : oldMsg
                                            )
                                        );
                                    }
                                });

                                skt.on("UpdateChatMessagesMarkAllAsSeen", ch => {
                                    if (ch.id == props.chatId) {
                                        messagesSet(prev => prev.map(m => ({ ...m, messagesStatus: "seen" })));
                                    }
                                });

                                // Read off the list as it is now, not as it was when this
                                // handler was made: the socket is wired once and the closure
                                // kept the empty list it was born with, so whoever started
                                // typing was written over and never taken off again.
                                skt.on("StartTyping-" + props.chatId, data => {
                                    if (data.id == user?.id) return;
                                    typingUsersSet(prev =>
                                        prev.some(t => t.id == data.id) ? prev : [...prev, data]);

                                    clearTypingTimer(data.id);
                                    typingTimers.current.set(data.id, setTimeout(() => {
                                        typingTimers.current.delete(data.id);
                                        typingUsersSet(prev => prev.filter(t => t.id != data.id));
                                    }, 5000));
                                });

                                skt.on("StopTyping-" + props.chatId, data => {
                                    clearTypingTimer(data.id);
                                    typingUsersSet(prev => prev.filter(t => t.id != data.id));
                                });

                            });

                    }, skt => {
                        skt.invoke("StopListenToChat", props.chatId)
                            .then(() => {
                                skt.off("UpdateChatMessage");
                                skt.off("SendChatMessage-" + props.chatId);
                                skt.off("StartTyping-" + props.chatId);
                                skt.off("StopTyping-" + props.chatId);
                            });
                    }, () => {
                        fetchMessages();
                    });


                return () => {
                    socket.stop(socketKey);
                    typingTimers.current.forEach(timer => clearTimeout(timer));
                    typingTimers.current.clear();
                    typingUsersSet([]);
                }
            }
        }

    }, [props.chat?.id, props.chat?.searchMsgItem])

    useEffect(() => {
        if (messages.length < 1) return;
        if (claiming) return;
        if (scrolling) return;
        if (loadingEarlierMessages) return;

        if (props.chat?.searchMsgItem) {
            gotoMessage(props.chat!.id!);
        }
        else if (props.chat?.id && loaded && !props.chat.searchMsgItem) {
            setTimeout(scrollToEnd, 50);
        }

    }, [props.chat?.id, props.chat?.searchMsgItem, loaded, messages])


    useEffect(() => {
        if (messages.length > 0 && !loading) {
            window.dataTableMenu()
        }
    }, [messages, loading])

    const [typingStarted, typingStartedSet] = useState<boolean>(false);
    useEffect(() => {
        if (typingStarted) {
            socket.get(skt => {
                skt.invoke("StartTyping", props.chatId, {
                    id: user?.id,
                    firstName: user?.firstName
                })
            })

            setTimeout(() => {
                socket.get(skt => {
                    skt.invoke("StopTyping", props.chatId, {
                        id: user?.id,
                        firstName: user?.firstName
                    })
                })
                typingStartedSet(false);
            }, 2000);
        }
    }, [typingStarted])

    const gotoMessage = (id: string) => {

        const msgView = document.getElementById(`chatmsg-${id}`) as HTMLDivElement | null;
        if (msgView) {
            msgView.scrollIntoView({
                behavior: "auto"
            })
        }

        // var m = document.getElementById("chatmsg-" + id);
        // m?.scrollIntoView({
        //     block: "center"
        // });

        //var i = messages.findIndex(f => f.id == id);

        setTimeout(() => {
            // chatContainer.current?.scrollToIndex({
            //     index: i,
            //     align: 'center',
            //     behavior: 'auto'
            // });
        }, 100);

        setTimeout(() => {
            var n = $("#chatmsg-" + id)
            n.find(".chat-message-text").addClass("job-note-blink");
            setTimeout(() => {
                n.find(".chat-message-text").addClass("job-note-blink-off");
                n.find(".chat-message-text").removeClass("job-note-blink");
            }, 300);
        }, 150);

    }

    const [text, textSet] = useState<string>()
    const send = (uuid: string, txt?: string) => {
        var textToSend = text;
        if (txt) textToSend = txt;
        if (!textToSend || textToSend == "") return;
        const replySnapshot = replyingTo;
        let msgs = messages ?? [];
        let msg: any = {
            chatId: props.chatId,
            senderUserId: user?.id,
            senderUser: {
                avatarUrl: user?.avatarUrl,
                firstName: user?.firstName,
                fullName: user?.fullName,
                id: user?.id
            },
            isMe: true,
            isTech: user?.id == props.chat?.userId,
            text: textToSend,
            jobId: props.jobId,
            uuid: uuid,
            isRecipient: user?.userType == UserTypes.Technician,
            replyToMessageId: replySnapshot?.id,
            replyToMessage: replySnapshot ? {
                id: replySnapshot.id,
                text: replySnapshot.text,
                senderUserId: replySnapshot.senderUserId,
                senderName: replySnapshot.senderUser?.fullName,
                mediaType: replySnapshot.media?.mediaType,
            } : undefined
        };
        msgs.push(msg)
        messagesSet(m => [...msgs]);
        textSet("");
        rowsSet(1);
        replyingToSet(undefined);
        chatTextArea?.current?.focus();
        setTimeout(scrollToEnd, 100);
        if (props.onMessageSending) props.onMessageSending(msg)

        hub.Post(API_ROUTES.ChatSend, {
            chatId: props.chatId,
            clientChatJobId: props.clientChatJobId,
            text: textToSend,
            jobId: props.jobId,
            uuid: uuid,
            replyToMessageId: replySnapshot?.id
        }, res => {
            if (res.success) {

                setTimeout(() => {
                    messagesSet(prev =>
                        prev.map(msg =>
                            msg.uuid === res.data.message.uuid ? res.data.message : msg
                        )
                    );
                }, 100);

                if (props.onMessageSent) props.onMessageSent(res.data.chat, res.data.message)

            }
            else {
                //failedSet(true)
            }
        })

    }



    const [rows, rowsSet] = useState<number>(1)
    useEffect(() => {
        var r = text?.split("\n").length ?? 1
        rowsSet(r)
    }, [text])

    useEffect(() => {
        fixSizes()
    }, [rows])


    const QuickRepliesContainer = useRef<HTMLDivElement>(null);

    const onWheel = (e: React.WheelEvent) => {
        if (!QuickRepliesContainer.current) return;
        e.preventDefault();
        QuickRepliesContainer.current.scrollLeft += e.deltaY;
    };


    const [workingHoursAlarm, workingHoursAlarmSet] = useState(false)
    useEffect(() => {
        if (props.chat?.tech) {
            hub.Get(`${API_ROUTES.Chat}workinghoursalarm/${props.chatId}`, res => workingHoursAlarmSet(res.data))
        }
        else workingHoursAlarmSet(false)
    }, [props.chat?.id])

    const [showWorkingHoursModal, showWorkingHoursModalSet] = useState(false)


    const [voiceRecorderOpen, voiceRecorderOpenSet] = useState(false)

    const [replyingTo, replyingToSet] = useState<ChatMessage>()

    useEffect(() => {
        replyingToSet(undefined)
    }, [props.chatId])

    useEffect(() => { fixSizes() }, [replyingTo])

    const startReply = (msg: ChatMessage) => {
        if (!msg.id) return;
        replyingToSet(msg)
        chatTextArea?.current?.focus()
    }

    return (
        <>
            <div className={`card ${(props.inDrawer ? "w-100" : "")}`} style={{ borderRadius: 0 }}>

                {
                    screen.isMobile ? <></>
                        : props.inDrawer ?
                            <div className="card-header d-none" id="kt_chat_messenger_header"
                                style={{
                                    minHeight: 40,
                                    height: 40
                                }}>
                                <div className="card-title">
                                    <FlexRow>
                                        {
                                            props.chat?.tech?.avatarUrl ?
                                                <div className="symbol symbol-25px symbol-circle">
                                                    <img alt={props.chat.tech?.fullName} src={props.chat.tech?.avatarUrl} />
                                                </div>
                                                :
                                                <div className="symbol symbol-25px symbol-circle">
                                                    {/* <span className="symbol-label bg-light-danger text-danger fs-4 fw-bolder">{props.chat?.title?.substring(0, 1).toUpperCase()}</span> */}
                                                    <ChatAvatarSpan item={props.chat} />
                                                </div>
                                        }
                                        <div className="d-flex justify-content-center flex-column ms-2 me-3">
                                            <FlexRow>
                                                <a className="fs-7 text-gray-900 me-1 lh-1">
                                                    {
                                                        isDemo() ? "Tech " + props.chat?.title?.substring(0, 2).toUpperCase() : props.chat?.title
                                                    }
                                                </a>
                                                {
                                                    props.chat?.isSystem &&
                                                    <Icon icon="badge-check" type="solid" className="text-primary" title="Verified Rinvox chat" />
                                                }
                                                {
                                                    (blockedNow ?? props.chat?.blocked) &&
                                                    <span className="badge badge-light-warning text-warning fs-9 ms-1">
                                                        <Icon icon="ban" size={9} className="text-warning me-1" />
                                                        Spam
                                                    </span>
                                                }
                                            </FlexRow>
                                        </div>
                                    </FlexRow>
                                </div>
                                <div className="card-toolbar">

                                </div>
                            </div>
                            :
                            <div className="card-header" id="kt_chat_messenger_header">
                                <div className="card-title">
                                    <FlexRow>
                                        {
                                            screen.isMobile &&
                                            <Link to={"/chat"} className="pe-5">
                                                <Icon icon="chevron-left" className="text-gray-700" size={20} />
                                            </Link>
                                        }
                                        <div className="symbol symbol-40px symbol-circle">
                                            {
                                                props.chat?.tech?.avatarUrl ?
                                                    <img alt={props.chat.tech?.fullName} src={props.chat.tech?.avatarUrl} />
                                                    :
                                                    <ChatAvatarSpan item={props.chat} />
                                            }

                                            {
                                                props.chat?.environment == ChatEnvironments.App &&
                                                <div className="symbol-badge text-center start-100 top-100 ms-n2 mt-n2">
                                                    <Icon icon="mobile-screen" doutone className="text-primary" size={16} />
                                                </div>
                                            }
                                        </div>

                                        <div className="d-flex flex-column ms-2 me-3 mt-2">
                                            <FlexRow>
                                                <a className="fs-6 fw-bolder text-gray-900 me-1 lh-1">
                                                    {
                                                        isDemo() ? "Tech " + props.chat?.title?.substring(0, 2).toUpperCase() : props.chat?.title
                                                    }
                                                </a>
                                                {
                                                    props.chat?.isSystem &&
                                                    <Icon icon="badge-check" type="solid" className="text-primary ms-1" title="Verified Rinvox chat" />
                                                }
                                                {
                                                    (blockedNow ?? props.chat?.blocked) &&
                                                    <span className="badge badge-light-warning text-warning fs-8 ms-2">
                                                        <Icon icon="ban" size={10} className="text-warning me-1" />
                                                        Spam
                                                    </span>
                                                }
                                            </FlexRow>
                                            {
                                                <div className="fs-8">
                                                    &nbsp;
                                                </div>
                                            }

                                        </div>
                                    </FlexRow>
                                </div>
                                <div className="card-toolbar">

                                    {
                                        props.chat?.tech &&
                                        <button type="button" className="btn btn-sm btn-icon btn-active-light-primary"
                                            onClick={e => showWorkingHoursModalSet(true)}>
                                            <Icon icon="calendar-clock" className="fs-2 text-gray-600" />
                                        </button>
                                    }

                                    <button type="button" className="btn btn-sm btn-icon btn-active-light-primary"
                                        onClick={e => {
                                            if (props.chat && props.onSearch) props.onSearch(props.chat);
                                        }}>
                                        <Icon icon="search" className="fs-2 text-gray-600" />
                                    </button>

                                    {
                                        canCallContact &&
                                        <button type="button" className="btn btn-sm btn-icon btn-active-light-primary"
                                            title={`Call ${props.chat?.recipientPhone}`}
                                            onClick={async () => {
                                                if (!props.chat?.recipientPhone) return;
                                                openBrowserPhone();
                                                try {
                                                    await phone.unlockAudio();
                                                    await phone.dial(props.chat.recipientPhone);
                                                }
                                                catch (err: any) {
                                                    // A refusal (no minutes, empty wallet) has already said why.
                                                    if (err?.name !== "CallRefused") toastError("Could not start the call.");
                                                }
                                            }}>
                                            <Icon icon="phone" className="fs-2 text-gray-600" />
                                        </button>
                                    }

                                    <div className="me-n3">
                                        <button className="btn btn-sm btn-icon btn-active-light-primary" data-kt-menu-trigger="click" data-kt-menu-placement="bottom-end">
                                            <i className="bi bi-three-dots fs-2"></i>
                                        </button>
                                        <ChatContactContextMenu chatId={props.chatId}
                                            chat={props.chat ? { ...props.chat, blocked: blockedNow ?? props.chat.blocked } : undefined}
                                            onPin={props.onPin}
                                            onUnpin={props.onUnpin}
                                            onUnarchive={props.onUnarchive} />
                                    </div>


                                </div>
                            </div>

                }

                <TechClientSessionBar chatId={props.chat?.id} currentJobId={props.clientChatJobId ?? props.jobId} watch={messages.length} onToggle={fixSizes} />

                <div className="card-body chat_messenger_body p-0 px-5 position-relative" id="kt_chat_messenger_body">

                    {
                        workingHoursAlarm &&
                        <FlexRow className="chat-warning-box position-absolute bg-light-warning w-100 p-3 text-gray-700" style={{ zIndex: 10 }}>
                            <div>
                                <Icon icon="calendar-clock" className="me-2 text-gray-700" size={18} />
                            </div>
                            <div>
                                This is outside the technician’s working hours. Responses may be delayed.
                            </div>
                            <div className="ms-auto">
                                <button type="button" className="btn btn-sm btn-dark"
                                    onClick={e => showWorkingHoursModalSet(true)}>
                                    View Working Hours
                                </button>
                            </div>
                        </FlexRow>
                    }

                    {
                        loading ?
                            <div className="pt-20" style={{ height: boxHeight }}>
                                <Indicator text="Loading Chat..." />
                            </div>
                            //: (screen.isMobile || messages.length < 30) ?
                            :
                            <div ref={chatContainerDiv}
                                className="scroll-y"
                                style={{
                                    height: boxHeight,
                                    overflowX: "hidden",
                                    paddingTop: 20
                                }}
                                onScroll={e => {
                                    if (e.currentTarget.scrollTop < 20) {
                                        fetchEarlierMessages()
                                    }

                                    if ((e.currentTarget.scrollTop + e.currentTarget.clientHeight + 1400) < e.currentTarget.scrollHeight) {
                                        scrollingSet(true)
                                    }
                                    else scrollingSet(false)

                                }}>

                                {
                                    loadingEarlierMessages && !loading &&
                                    <div className="mt-5 mb-10">
                                        <Indicator text="Loading Earlier Messages..." />
                                    </div>
                                }

                                {
                                    messages.map((msg, k) =>
                                        <ChatMessageItem key={msg.uuid} message={msg} chat={props.chat!} lastSender={k > 0 ? messages[k - 1].senderUser : undefined}
                                            onAssignToJob={props.onAssignMessageToJob}
                                            onReply={startReply}
                                            onClaiming={e => claimingSet(true)} isGroup={props.chat?.isGroup}
                                            onClaimed={e => {
                                                setTimeout(() => {
                                                    claimingSet(false)
                                                }, 500);
                                            }} />
                                    )
                                }

                                {/* At the end of the thread, where their message will appear —
                                    the technician's on the left with theirs, the office's on the
                                    right with ours. Who is writing is the point here: a room of
                                    dispatchers and admins all write into the same conversation,
                                    and a name is what tells one colleague from another. */}
                                {
                                    typingUsers.filter(t => t.id == props.chat?.techId).length > 0 &&
                                    <ChatTypingBubble names={[]} />
                                }

                                {
                                    typingUsers.filter(t => t.id != props.chat?.techId).length > 0 &&
                                    <ChatTypingBubble mine
                                        names={typingUsers.filter(t => t.id != props.chat?.techId).map(t => t.firstName)} />
                                }

                            </div>


                    }

                    {
                        scrolling &&
                        <IconButton className="btn-chat-to-end btn-active" icon="arrow-down" size={26} gap={26}
                            onClick={e => scrollToEnd("smooth")} />
                    }
                    {
                        scrolling && (props.chat?.unreadCount ?? 0) > 0 && user?.userType != UserTypes.Technician &&
                        <div className="btn-chat-to-end-newmsg">
                            {props.chat?.unreadCount}
                        </div>
                    }

                </div>

                <div className="card-footer pt-2 px-2" id="kt_chat_messenger_footer">

                    {
                        replyingTo &&
                        <div className="chat-reply-banner">
                            <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                <div className="chat-reply-banner-sender">
                                    <Icon icon="reply" className="me-1" size={11} />
                                    Replying to {isDemo() ? "User" : (replyingTo.senderUser?.fullName ?? "Unknown")}
                                </div>
                                <div className="chat-reply-banner-text">
                                    {
                                        replyingTo.media?.mediaType == MediaTypes.Image ? <><Icon icon="camera" size={10} className="me-1" />Photo</>
                                            : replyingTo.media?.mediaType == MediaTypes.Video ? <><Icon icon="camera-movie" size={10} className="me-1" />Video</>
                                                : replyingTo.media?.mediaType == MediaTypes.Audio ? <><Icon icon="volume" size={10} className="me-1" />Voice message</>
                                                    : replyingTo.media ? <><Icon icon="paperclip-vertical" size={10} className="me-1" />{replyingTo.media.fileName}</>
                                                        : replyingTo.text
                                    }
                                </div>
                            </div>
                            <a href="#" className="ms-2 text-gray-700"
                                onClick={e => {
                                    e.preventDefault();
                                    replyingToSet(undefined);
                                }}>
                                <Icon icon="times" size={14} />
                            </a>
                        </div>
                    }

                    {
                        (replies.length > 0 || hasRole("ManageChatQuickReplies")) && !user?.isTech && !props.chat?.readOnly &&
                        <div className="row border-bottom pb-3 pt-3 mb-5">
                            <div className="col-10 col-md-11">
                                <div ref={QuickRepliesContainer}
                                    className="d-flex quick-replies-container"
                                    onWheel={onWheel}>
                                    {
                                        replies.map(r =>
                                            <div key={`btn-quick-reply-${r.id}`}
                                                title={r.text}
                                                className="badge badge-light-primary cursor-pointer me-2"
                                                onClick={e => {
                                                    e.preventDefault()
                                                    send(uuidv4(), r.text)
                                                }}>
                                                {r.title}
                                                {
                                                    r.title != r.text && <Icon icon="info-circle" className="ms-1" />
                                                }
                                            </div>
                                        )
                                    }

                                    {
                                        hasRole("ManageChatQuickReplies") &&
                                        <div className="badge badge-secondary cursor-pointer"
                                            onClick={openChatQuickReplies}>
                                            <Icon icon="plus" className="me-1" />
                                            <span className="fw-normal">Add Quick Reply</span>
                                        </div>
                                    }

                                </div>
                            </div>
                        </div>
                    }




                    {
                        voiceRecorderOpen ?
                            <VoiceRecorder
                                onClose={() => {
                                    voiceRecorderOpenSet(false)
                                }}
                                onSubmit={file => {

                                    const replySnapshot = replyingTo;
                                    var msgs = messages ?? [];

                                    var msg: any = {
                                        media: {
                                            fileName: file.name
                                        },
                                        fileToUpload: file,
                                        chatId: props.chatId,
                                        senderUserId: user?.id,
                                        senderUser: {
                                            avatarUrl: user?.avatarUrl,
                                            firstName: user?.firstName,
                                            fullName: user?.fullName,
                                            id: user?.id,
                                            username: user?.username
                                        },
                                        id: props.chatId,
                                        uploading: true,
                                        uuid: uuidv4(),
                                        replyToMessageId: replySnapshot?.id,
                                        replyToMessage: replySnapshot ? {
                                            id: replySnapshot.id,
                                            text: replySnapshot.text,
                                            senderUserId: replySnapshot.senderUserId,
                                            senderName: replySnapshot.senderUser?.fullName,
                                            mediaType: replySnapshot.media?.mediaType,
                                        } : undefined
                                    }
                                    msgs.push(msg);
                                    replyingToSet(undefined);
                                    if (props.onMessageSending) props.onMessageSending(msg)

                                    hub.Upload({
                                        category: "chatvoice",
                                        pathId: props.chatId,
                                        file: file
                                    }, res => {
                                        if (res.success && res.data) {

                                            hub.Post(API_ROUTES.ChatSend, {
                                                chatId: props.chatId,
                                                text: "",
                                                fileName: file.name,
                                                filePath: res.data,
                                                isRecordingVoice: "yes",
                                                uuid: msg.uuid,
                                                replyToMessageId: replySnapshot?.id
                                            }, res => {
                                                setTimeout(() => {

                                                    messagesSet(prev =>
                                                        prev.map(msg =>
                                                            msg.uuid === res.data.message.uuid ? res.data.message : msg
                                                        )
                                                    );

                                                    if (props.onMessageSent) props.onMessageSent(res.data.chat, res.data.message)

                                                }, 100);
                                            });
                                        }
                                    });


                                    messagesSet(m => [...msgs]);

                                }} />
                            : (blockedNow ?? props.chat?.blocked) ?
                                <FlexRow className="py-3">
                                    <Icon icon="ban" className="text-warning me-2" size={13} />
                                    <div className="fs-7 text-gray-700 flex-grow-1">
                                        This chat is marked as spam. Its messages are still received, but stay out of the chat list and send no notifications.
                                    </div>
                                    {
                                        hasRole("ArchiveChat") &&
                                        <button type="button" className="btn btn-sm btn-light-warning ms-3 text-nowrap"
                                            onClick={() => unblockChat(hub, props.chat)}>
                                            Not Spam
                                        </button>
                                    }
                                </FlexRow>
                            : props.chat?.readOnly ?
                                <div className="text-center text-gray-500 fs-8 py-3">
                                    <Icon icon="lock" type="solid" size={11} className="me-2" />
                                    Only Rinvox writes here
                                </div>
                            : (user?.isCustomer || user?.isTech || user?.isSystemAdmin || props.chat?.accessAll || props.chat?.accessClaimUserIds?.includes(user?.id ?? "0000")) ?
                                <FlexRow>
                                    <div className="flex-grow-1 me-2">
                                        <textarea id="txt-chatbox-message" ref={chatTextArea} className="form-control form-control-flush mb-3" data-gramm="false"
                                            rows={rows} placeholder="Write a message" value={text} onChange={e => textSet(e.target.value)} autoFocus={!isNativeApp()}
                                            style={{
                                                resize: "none"
                                            }}
                                            onKeyDown={e => {
                                                typingStartedSet(true);

                                                if (isNativeApp()) return;

                                                if (e.key == "Enter") {
                                                    e.preventDefault();
                                                    var txt = $("#txt-chatbox-message");
                                                    if (e.ctrlKey || e.shiftKey || e.altKey) {
                                                        const textarea = e.currentTarget;
                                                        const cursorPosition = textarea.selectionStart;
                                                        const currentText = text ?? "";
                                                        const newText = currentText.slice(0, cursorPosition) + "\n" + currentText.slice(cursorPosition);
                                                        textSet(newText);
                                                        setTimeout(() => {
                                                            textarea.selectionStart = textarea.selectionEnd = cursorPosition + 1;
                                                        }, 10);
                                                    }
                                                    else {
                                                        send(uuidv4())
                                                    }
                                                }
                                            }}
                                        ></textarea>
                                    </div>
                                    <FlexRow>

                                        {
                                            (text && text.length > 0) ?
                                                <>
                                                    <IconButton className="bg-primary text-white" icon="send" type="solid" size={18} gap={26}
                                                        onClick={() => send(uuidv4())} />
                                                </>
                                                :
                                                <>
                                                    <IconButton icon="plus" type="solid" size={16} gap={20}
                                                        onClick={() => {
                                                            chatAttachmentInput?.click();
                                                        }} />

                                                    <IconButton icon="microphone" type="solid" size={16} gap={20}
                                                        onClick={() => {
                                                            voiceRecorderOpenSet(true)
                                                        }} />
                                                </>
                                        }

                                    </FlexRow>

                                    <div className="d-none">
                                        <input ref={e => chatAttachmentInput = e} name="file" type="file" multiple
                                            accept="image/png, image/jpeg, application/pdf"
                                            onChange={e => {
                                                if (e.target.files) {
                                                    var files = Array.from(e.target.files);
                                                    var msgs = messages ?? [];
                                                    const replySnapshot = replyingTo;
                                                    files.forEach((file, fileIdx) => {

                                                        const useReply = fileIdx === 0 ? replySnapshot : undefined;
                                                        var msg: any = {
                                                            media: {
                                                                fileName: file.name
                                                            },
                                                            fileToUpload: file,
                                                            chatId: props.chatId,
                                                            senderUserId: user?.id,
                                                            senderUser: {
                                                                avatarUrl: user?.avatarUrl,
                                                                firstName: user?.firstName,
                                                                fullName: user?.fullName,
                                                                id: user?.id,
                                                                username: user?.username
                                                            },
                                                            id: props.chatId,
                                                            uploading: true,
                                                            uuid: uuidv4(),
                                                            replyToMessageId: useReply?.id,
                                                            replyToMessage: useReply ? {
                                                                id: useReply.id,
                                                                text: useReply.text,
                                                                senderUserId: useReply.senderUserId,
                                                                senderName: useReply.senderUser?.fullName,
                                                                mediaType: useReply.media?.mediaType,
                                                            } : undefined
                                                        }
                                                        msgs.push(msg);
                                                        if (props.onMessageSending) props.onMessageSending(msg)

                                                        hub.Upload({
                                                            category: "chat",
                                                            pathId: props.chatId,
                                                            file: file
                                                        }, res => {
                                                            if (res.success && res.data) {
                                                                hub.Post(API_ROUTES.ChatSend, {
                                                                    chatId: props.chatId,
                                                                    text: "",
                                                                    fileName: file.name,
                                                                    filePath: res.data,
                                                                    uuid: msg.uuid,
                                                                    replyToMessageId: useReply?.id
                                                                }, res => {
                                                                    setTimeout(() => {

                                                                        messagesSet(prev =>
                                                                            prev.map(msg =>
                                                                                msg.uuid === res.data.message.uuid ? res.data.message : msg
                                                                            )
                                                                        );

                                                                        if (props.onMessageSent) props.onMessageSent(res.data.chat, res.data.message)

                                                                    }, 100);
                                                                });
                                                            }
                                                        });

                                                    });
                                                    messagesSet(m => [...msgs]);
                                                    if (replySnapshot) replyingToSet(undefined);
                                                    if (chatAttachmentInput) chatAttachmentInput.value = "";
                                                }
                                            }} />
                                    </div>
                                </FlexRow>
                                : <></>
                    }

                </div>
            </div>

            {
                showWorkingHoursModal && props.chat?.tech?.id &&
                <TechWorkingHoursDrawer techId={props.chat.tech.id}
                    onClose={() => showWorkingHoursModalSet(false)} />
            }

        </>
    )
}